import type { ReactNode } from 'react'
import { cn } from '@workflow/ui'
import { OPS_PAGE_MEASURE } from './layout'

/**
 * The frame every page sits in: one serif statement, an optional line under
 * it, actions on the right, and a column no wider than the page measure.
 * Keep the content measure steady when the sidebar opens. The page panel
 * moves, while cards and text retain their size instead of reflowing.
 */
export function PageFrame({
  title,
  eyebrow,
  description,
  actions,
  children,
  className,
  measure,
}: {
  title: ReactNode
  eyebrow?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  /** Wider work surfaces, such as a payment sheet, can use their own measure. */
  measure?: number
}) {
  return (
    <div
      className={cn('mx-auto w-full px-4 pt-20 pb-16 sm:px-8 sm:pt-24', className)}
      style={{
        maxWidth: measure ?? OPS_PAGE_MEASURE,
      }}
    >
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow ? <div className="mb-2">{eyebrow}</div> : null}
          <h1 className="wf-display text-foreground">{title}</h1>
          {description ? <p className="mt-1.5 max-w-[60ch] text-ui text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </header>
      {children}
    </div>
  )
}

/** A titled group on a page. */
export function Section({
  title,
  aside,
  children,
  className,
}: {
  title: string
  aside?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('mt-10 first:mt-0', className)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-micro text-muted-foreground uppercase">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}
